import { OFFICIALS_PAGE_SIZE_DEFAULT, type OfficialItem } from "@seagames/contracts";
import { useFilterDraft } from "@seagames/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { PAGE_SIZE_CHOICES, pageBeyondEnd, pageToUrl, sizeToUrl } from "../../components/nocs/list-params";
import { nocOptionsQuery } from "../../components/nocs/queries";
import { useOnGamesSwitch, useScrollToTopOnPage } from "../../components/nocs/use-list-page";
import { OfficialDrawer } from "../../components/officials/official-drawer";
import { OfficialsFilters } from "../../components/officials/officials-filters";
import { OfficialsTable } from "../../components/officials/officials-table";
import { officialsListQuery } from "../../components/officials/queries";
import {
	EMPTY_OFFICIALS_DRAFT,
	type OfficialsDraft,
	type OfficialsSearch,
	officialsDraftFrom,
	officialsFilterCount,
	officialsPaging,
	officialsParams,
	officialsSearch,
	officialsSearchFrom,
} from "../../components/officials/search";
import { PageHeader } from "../../components/page/page-header";
import { Button } from "../../components/ui/button";
import { EmptyState } from "../../components/ui/empty-state";
import { Spinner } from "../../components/ui/spinner";
import { ApiError } from "../../lib/api/client";
import { errorMessage } from "../../lib/api/messages";
import { gamesSportsQuery, useCurrentGames } from "../../lib/games";
import { formatInteger } from "../../lib/i18n/format";

/**
 * Participants › Officials (the old OVR's "Judges") — the technical officials
 * of the current games, one page at a time (`GET /officials`, offset pages
 * with the total), and a drawer with one official's seats on the meets
 * (`GET /officials/:acCardId`). Filters, the page and the open official live
 * in the URL (`?q&noc&sport&page&size&official`). Read-only: officials are
 * accredited in GMS and seated on each meet's screen.
 */
export const Route = createFileRoute("/_app/officials")({
	validateSearch: officialsSearch,
	component: OfficialsPage,
});

function OfficialsPage() {
	const { t } = useTranslation("officials");
	const search = Route.useSearch();
	const navigate = useNavigate({ from: "/officials" });
	const current = useCurrentGames();
	const gamesId = current.gamesId;

	const sports = useQuery({ ...gamesSportsQuery(gamesId ?? ""), enabled: gamesId !== null });
	const nocs = useQuery({ ...nocOptionsQuery(gamesId ?? ""), enabled: gamesId !== null });

	const go = useCallback((next: OfficialsSearch, replace = false) => void navigate({ search: next, replace }), [navigate]);
	const applied = useMemo(() => officialsDraftFrom(search), [search]);
	const onApply = useCallback((d: OfficialsDraft) => go(officialsSearchFrom(d, { size: search.size })), [go, search.size]);
	const f = useFilterDraft<OfficialsDraft>(applied, onApply, EMPTY_OFFICIALS_DRAFT);
	const filterCount = officialsFilterCount(search);

	// The sidebar switched games: sports and the open official belong to the old one.
	useOnGamesSwitch(gamesId, () => go({ ...search, sport: undefined, official: undefined, page: undefined }, true));

	const params = gamesId !== null ? officialsParams(gamesId, search) : null;
	const list = useQuery({
		...officialsListQuery(params ?? { games_id: "", page: 1, page_size: OFFICIALS_PAGE_SIZE_DEFAULT }),
		enabled: params !== null,
		placeholderData: keepPreviousData,
	});

	const { page, pageSize } = officialsPaging(search);
	const beyond = list.data && list.data.items.length === 0 ? pageBeyondEnd(page, list.data.total, pageSize) : null;
	useEffect(() => {
		if (beyond !== null) go({ ...search, page: pageToUrl(beyond) }, true);
	}, [beyond]);

	const top = useRef<HTMLDivElement>(null);
	useScrollToTopOnPage(top, page);

	const open = (o: OfficialItem) => go({ ...search, official: o.ac_card_id });
	const close = () => go({ ...search, official: undefined });

	if (current.error) {
		return (
			<div className="flex flex-col">
				<PageHeader title={t("title")} />
				<EmptyState title={t("errors.gamesTitle")} body={errorMessage(current.error)} />
			</div>
		);
	}
	if (current.isLoading) return <Spinner className="size-[1.5rem] text-board" />;
	if (!current.games || gamesId === null) {
		return (
			<div className="flex flex-col">
				<PageHeader title={t("title")} />
				<EmptyState title={t("empty.noGamesTitle")} body={t("empty.noGamesBody")} />
			</div>
		);
	}
	// A role that may not read the register (a NOC login): say so instead of a red error row.
	if (list.error instanceof ApiError && list.error.code === "FORBIDDEN_ROLE") {
		return (
			<div className="flex flex-col">
				<PageHeader title={t("title")} />
				<EmptyState title={t("empty.forbiddenTitle")} body={errorMessage(list.error)} />
			</div>
		);
	}

	const total = list.data?.total ?? 0;
	const rows = list.data?.items ?? [];

	return (
		<div className="flex flex-col">
			<PageHeader title={t("title")} subtitle={t("subtitle", { games: current.games.name })} />

			<div className="flex flex-col gap-[1rem]">
				<OfficialsFilters
					draft={f.draft}
					set={f.set}
					onApply={f.apply}
					onReset={f.reset}
					activeCount={filterCount}
					sports={sports.data ?? []}
					nocs={nocs.data?.items ?? []}
				/>

				<div ref={top} className="scroll-mt-[4.5rem]">
					<OfficialsTable
						rows={rows}
						loading={list.isPending || (list.isFetching && list.isPlaceholderData)}
						error={list.isError ? { message: errorMessage(list.error), onRetry: () => void list.refetch() } : null}
						empty={
							filterCount > 0
								? {
										title: t("empty.filteredTitle"),
										description: t("empty.filteredBody"),
										action: (
											<Button variant="outline" size="sm" onClick={f.reset}>
												{t("empty.reset")}
											</Button>
										),
									}
								: { title: t("empty.noneTitle", { games: current.games.name }), description: t("empty.noneBody") }
						}
						toolbar={
							list.data ? (
								<span className="text-[0.8125rem] text-muted" aria-live="polite">
									{t("table.count", { count: total, n: formatInteger(total) })}
								</span>
							) : null
						}
						pagination={
							list.data && total > 0
								? {
										page,
										pageSize,
										total,
										onPageChange: (p) => go({ ...search, page: pageToUrl(p) }),
										onPageSizeChange: (s) => go({ ...search, size: sizeToUrl(s, OFFICIALS_PAGE_SIZE_DEFAULT), page: undefined }),
										pageSizeOptions: PAGE_SIZE_CHOICES,
									}
								: undefined
						}
						onOpen={open}
					/>
				</div>
			</div>

			{search.official && <OfficialDrawer key={search.official} gamesId={gamesId} acCardId={search.official} onClose={close} />}
		</div>
	);
}
